import React, { useState, useEffect } from 'react';
import { X, Star, EyeOff } from 'lucide-react';
import { DrillDifficulty, DrillCategory, TrainingDrill, AgeGroup } from '../Model';
import { SortOption, filterDrills, sortDrills } from './drillSorting';
import { useReferenceData } from '../ReferenceDataContext';

interface DrillFiltersProps {
  drills: TrainingDrill[];
  onFilteredDrillsChange: (filteredDrills: TrainingDrill[]) => void;
  favouriteDrillIds?: Set<number>;
  isAdmin?: boolean;
  showHidden?: boolean;
  onShowHiddenChange?: (show: boolean) => void;
}

export const DrillFilters: React.FC<DrillFiltersProps> = ({
  drills,
  onFilteredDrillsChange,
  favouriteDrillIds,
  isAdmin,
  showHidden,
  onShowHiddenChange,
}) => {
  const { referenceData } = useReferenceData();
  const [searchQuery, setSearchQuery] = useState('');
  const [filterDifficulty, setFilterDifficulty] = useState<DrillDifficulty | 'All'>('All');
  const [filterCategory, setFilterCategory] = useState<DrillCategory | 'All'>('All');
  const [filterAgeGroup, setFilterAgeGroup] = useState<AgeGroup | 'All'>('All');
  const [sortBy, setSortBy] = useState<SortOption>('name');
  const [showFavouritesOnly, setShowFavouritesOnly] = useState(false);
  const [filteredCount, setFilteredCount] = useState(drills.length);

  // Calculate filtered drills and notify parent whenever filters change
  useEffect(() => {
    let result = drills;

    // Apply favourites filter first
    if (showFavouritesOnly && favouriteDrillIds) {
      result = result.filter(drill => favouriteDrillIds.has(drill.id));
    }

    const filtered = sortDrills(
      filterDrills(result, searchQuery, filterDifficulty, filterCategory, filterAgeGroup),
      sortBy
    );
    setFilteredCount(filtered.length);
    onFilteredDrillsChange(filtered);
  }, [drills, searchQuery, filterDifficulty, filterCategory, filterAgeGroup, sortBy, showFavouritesOnly, favouriteDrillIds, onFilteredDrillsChange]);

  // Check if any filters are active (showHidden is a view toggle, not a filter)
  const hasActiveFilters = filterDifficulty !== 'All' || filterCategory !== 'All' || filterAgeGroup !== 'All' || searchQuery !== '' || showFavouritesOnly;

  // Clear all filters (does not affect showHidden view toggle)
  const clearAllFilters = () => {
    setSearchQuery('');
    setFilterDifficulty('All');
    setFilterCategory('All');
    setFilterAgeGroup('All');
    setShowFavouritesOnly(false);
  };

  return (
    <>
      <div className="toolbar">
        <div className="toolbar-filters">
          <div className="search-input">
            <input
              type="text"
              placeholder="Search by name, skill, or keyword..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="form-control"
            />
          </div>

          <div className="filter-group">
            <label htmlFor="difficulty-filter" className="filter-label">Difficulty:</label>
            <select
              id="difficulty-filter"
              value={filterDifficulty}
              onChange={(e) => setFilterDifficulty(e.target.value as DrillDifficulty | 'All')}
              className="form-select form-select-inline"
            >
              <option value="All">All Levels</option>
              {(referenceData?.difficulties ?? []).map(d => (
                <option key={d} value={d}>{d}</option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label htmlFor="category-filter" className="filter-label">Category:</label>
            <select
              id="category-filter"
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value as DrillCategory | 'All')}
              className="form-select form-select-inline"
            >
              <option value="All">All Categories</option>
              {(referenceData?.categories ?? []).map(c => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label htmlFor="age-group-filter" className="filter-label">Age:</label>
            <select
              id="age-group-filter"
              value={filterAgeGroup}
              onChange={(e) => setFilterAgeGroup(e.target.value as AgeGroup | 'All')}
              className="form-select form-select-inline"
            >
              <option value="All">All Ages</option>
              {(referenceData?.ageGroups ?? []).map(ag => (
                <option key={ag.value} value={ag.value}>{ag.label}</option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label htmlFor="sort-by" className="filter-label">Sort:</label>
            <select
              id="sort-by"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as SortOption)}
              className="form-select form-select-inline"
            >
              <option value="name">Name (A-Z)</option>
              <option value="duration-asc">Duration (Short)</option>
              <option value="duration-desc">Duration (Long)</option>
              <option value="difficulty-asc">Easy first</option>
              <option value="difficulty-desc">Hard first</option>
            </select>
          </div>

          {favouriteDrillIds && (
            <button
              className={`btn btn-sm ${showFavouritesOnly ? 'btn-warning' : 'btn-outline-secondary'} drill-favourites-toggle`}
              onClick={() => setShowFavouritesOnly(!showFavouritesOnly)}
              aria-label={showFavouritesOnly ? 'Show all drills' : 'Show favourites only'}
              title={showFavouritesOnly ? 'Show all drills' : 'Show favourites only'}
            >
              <Star size={14} fill={showFavouritesOnly ? 'currentColor' : 'none'} />
            </button>
          )}

          {isAdmin && onShowHiddenChange && (
            <button
              className={`btn btn-sm ${showHidden ? 'btn-warning' : 'btn-outline-secondary'} drill-favourites-toggle`}
              onClick={() => onShowHiddenChange(!showHidden)}
              aria-label={showHidden ? 'Hide hidden drills' : 'Show hidden drills'}
              title={showHidden ? 'Hide hidden drills' : 'Show hidden drills'}
            >
              <EyeOff size={14} />
            </button>
          )}

          <span className="filter-count ml-auto">
            {filteredCount} of {drills.length}
          </span>
        </div>
      </div>

      {/* Active Filter Chips */}
      {(hasActiveFilters || showHidden) && (
        <div className="active-filters">
          {showHidden && (
            <span className="filter-chip">
              Showing hidden
              <button onClick={() => onShowHiddenChange?.(false)} className="filter-chip-remove" aria-label="Remove hidden filter">
                <X size={12} />
              </button>
            </span>
          )}
          {showFavouritesOnly && (
            <span className="filter-chip">
              Favourites
              <button onClick={() => setShowFavouritesOnly(false)} className="filter-chip-remove" aria-label="Remove favourites filter">
                <X size={12} />
              </button>
            </span>
          )}
          {searchQuery && (
            <span className="filter-chip">
              Search: "{searchQuery}"
              <button onClick={() => setSearchQuery('')} className="filter-chip-remove" aria-label="Remove search filter">
                <X size={12} />
              </button>
            </span>
          )}
          {filterDifficulty !== 'All' && (
            <span className="filter-chip">
              {filterDifficulty}
              <button onClick={() => setFilterDifficulty('All')} className="filter-chip-remove" aria-label="Remove difficulty filter">
                <X size={12} />
              </button>
            </span>
          )}
          {filterCategory !== 'All' && (
            <span className="filter-chip">
              {filterCategory}
              <button onClick={() => setFilterCategory('All')} className="filter-chip-remove" aria-label="Remove category filter">
                <X size={12} />
              </button>
            </span>
          )}
          {filterAgeGroup !== 'All' && (
            <span className="filter-chip">
              {filterAgeGroup}
              <button onClick={() => setFilterAgeGroup('All')} className="filter-chip-remove" aria-label="Remove age filter">
                <X size={12} />
              </button>
            </span>
          )}
          {hasActiveFilters && (
            <button onClick={clearAllFilters} className="filter-clear-all">
              Clear all
            </button>
          )}
        </div>
      )}
    </>
  );
};
